// frontend/src/pages/DashboardPage.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
  ChartOptions,
  ChartData,
  Plugin,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { KpiCard } from '../components/Dashboard/KpiCard';
import { ChartContainer } from '../components/Dashboard/ChartContainer';
import '../components/Dashboard/Dashboard.css';
import { authFetch } from '../utils/authFetch';

// Registrar Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, Title, ChartDataLabels, CategoryScale, LinearScale, BarElement);

// ---------- Tipos ----------
interface LeadTimeData {
  supplier: string;
  shipper: string;
  avg_tt: number;
  avg_delivery_days: number;
}
interface ChannelApiItem {
  channel: string;
  count: number;
  percentage: number;
}
interface SupplierApi {
  total_processes: number;
  data: { label: string; value: number }[];
}

// ---------- Utils ----------
const generateColors = (numColors: number) => {
  const colors: string[] = [];
  for (let i = 0; i < numColors; i++) {
    const hue = (i * (360 / (numColors * 1.618))) % 360;
    colors.push(`hsla(${hue}, 70%, 55%, 0.9)`);
  }
  return colors;
};

const numberFmt = (n: number | string) =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(typeof n === 'string' ? Number(n) : n);

const makeVerticalGradient = (ctx: CanvasRenderingContext2D, area: any, baseColor: string) => {
  const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
  const soft = baseColor.replace(/([\d.]+)\)$/,'0.2)'); // troca alpha pra 0.2 se for rgba/hsla(...)
  gradient.addColorStop(0, soft);
  gradient.addColorStop(1, baseColor);
  return gradient;
};

// Plugin: aplica gradiente só em gráficos de barra (corrige o erro de TS)
const gradientPlugin: Plugin = {
  id: 'ccomexGradient',
  beforeDatasetsDraw(chart) {
    const chartType = (chart as any)?.config?.type as string | undefined;
    if (chartType !== 'bar') return; // só barras

    const ctx = chart.ctx as CanvasRenderingContext2D;
    const area = (chart as any).chartArea;
    if (!area) return;

    chart.data.datasets.forEach((ds: any) => {
      if (Array.isArray(ds.backgroundColor)) {
        ds.backgroundColor = ds.backgroundColor.map((c: string) => makeVerticalGradient(ctx, area, c));
      } else if (typeof ds.backgroundColor === 'string') {
        ds.backgroundColor = makeVerticalGradient(ctx, area, ds.backgroundColor);
      }
    });
  },
};
ChartJS.register(gradientPlugin);

// ---------- Componente ----------
export const DashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState<{ active_processes?: number; on_time_percentage?: number }>({});
  const [leadTimeData, setLeadTimeData] = useState<LeadTimeData[]>([]);
  const [channelChartData, setChannelChartData] = useState<ChartData<'doughnut'>>({ labels: [], datasets: [] });
  const [supplierData, setSupplierData] = useState<ChartData<'doughnut'>>({ labels: [], datasets: [] });
  const [statusData, setStatusData] = useState<ChartData<'bar'>>({ labels: [], datasets: [] });
  const [cadastroData, setCadastroData] = useState<ChartData<'bar'>>({ labels: [], datasets: [] });
  const [plannerData, setPlannerData] = useState<ChartData<'doughnut'>>({ labels: [], datasets: [] });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          kpiRes,
          leadTimeRes,
          chanRes,
          supRes,
          statRes,
          cadRes,
          planRes,
        ] = await Promise.all([
          authFetch('/api/dashboard/kpis').then((res) => res.json()),
          authFetch('/api/dashboard/leadtime-performance').then((res) => res.json()),
          authFetch('/api/dashboard/by-desembaraco-channel?active_only=false&debug=true').then((res) => res.json()),
          authFetch('/api/dashboard/by-supplier').then((res) => res.json()),
          authFetch('/api/dashboard/by-status').then((res) => res.json()),
          authFetch('/api/dashboard/by-cadastro').then((res) => res.json()),
          authFetch('/api/dashboard/by-planner').then((res) => res.json()),
        ]);

        // KPIs
        setKpis(kpiRes ?? {});
        // Leadtime
        setLeadTimeData(Array.isArray(leadTimeRes) ? leadTimeRes : []);

        // Canal de Desembaraço
        console.log('by-desembaraco-channel =>', chanRes);
        if (Array.isArray(chanRes) && chanRes.length > 0) {
          const labels = chanRes.map((c: ChannelApiItem) => `${c.channel.replace('CANAL ', '')} (${c.count})`);
          const data = chanRes.map((c: ChannelApiItem) => c.count);
          const backgroundColors = chanRes.map((c: ChannelApiItem) => {
            const label = (c.channel || '').toUpperCase();
            if (label.includes('VERDE')) return 'hsla(140, 72%, 45%, 0.9)';
            if (label.includes('AMARELO')) return 'hsla(45, 95%, 52%, 0.9)';
            if (label.includes('VERMELHO')) return 'hsla(0, 82%, 56%, 0.9)';
            return 'hsla(210, 10%, 60%, 0.9)';
          });

          setChannelChartData({
            labels,
            datasets: [
              {
                data,
                backgroundColor: backgroundColors,
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverOffset: 8,
              },
            ],
          });
        } else {
          setChannelChartData({ labels: [], datasets: [] });
        }

        // Fornecedor
        const sup = supRes as SupplierApi;
        if (sup?.data?.length) {
          const supLabels = sup.data.map(
            (d: any) => `${d.label} (${((d.value / (sup.total_processes || 1)) * 100).toFixed(1)}%)`
          );
          setSupplierData({
            labels: supLabels,
            datasets: [
              {
                data: sup.data.map((d: any) => d.value),
                backgroundColor: generateColors(sup.data.length),
                borderColor: '#fff',
                borderWidth: 2,
              },
            ],
          });
        } else {
          setSupplierData({ labels: [], datasets: [] });
        }

        // Status
        if (Array.isArray(statRes) && statRes.length) {
          setStatusData({
            labels: statRes.map((d: any) => (d.label || '').toString().replace(/^\d+\)\s*/, '')),
            datasets: [
              {
                label: 'Nº de Processos',
                data: statRes.map((d: any) => d.value),
                backgroundColor: 'hsla(210, 54%, 32%, 0.9)', // #25517d
                borderRadius: 8,
              },
            ],
          });
        } else {
          setStatusData({ labels: [], datasets: [] });
        }

        // Cadastro
        if (Array.isArray(cadRes) && cadRes.length) {
          setCadastroData({
            labels: cadRes.map((d: any) => d.label),
            datasets: [
              {
                label: 'Nº de Processos',
                data: cadRes.map((d: any) => d.value),
                backgroundColor: 'hsla(210, 29%, 24%, 0.9)', // #2c3e50
                borderRadius: 8,
              },
            ],
          });
        } else {
          setCadastroData({ labels: [], datasets: [] });
        }

        // Planejador
        if (Array.isArray(planRes) && planRes.length) {
          setPlannerData({
            labels: planRes.map((d: any) => d.label),
            datasets: [
              {
                data: planRes.map((d: any) => d.value),
                backgroundColor: generateColors(planRes.length),
                borderColor: '#fff',
                borderWidth: 2,
              },
            ],
          });
        } else {
          setPlannerData({ labels: [], datasets: [] });
        }
      } catch (err: any) {
        console.error('Erro ao carregar dados do dashboard:', err);
        setError('Falha ao carregar dados do dashboard.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // ---------- Opções modernizadas ----------
  const baseDonutOptions: ChartOptions<'doughnut'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      animation: { animateRotate: true, animateScale: true, duration: 800, easing: 'easeOutQuart' },
      plugins: {
        legend: {
          display: true,
          position: 'right',
          labels: { usePointStyle: true, boxWidth: 10, padding: 16 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const value = ctx.parsed || 0;
              const dataset = ctx.chart.data.datasets?.[ctx.datasetIndex || 0] as any;
              const sum = (dataset?.data || []).reduce((a: number, b: number) => a + (Number.isFinite(b) ? b : 0), 0);
              const pct = sum ? ((value / sum) * 100).toFixed(1) : '0.0';
              const label = ctx.label?.toString().replace(/\s*\(\d+\)\s*$/, '') || '';
              return `${label}: ${numberFmt(value)} (${pct}%)`;
            },
          },
        },
        datalabels: {
          color: '#fff',
          font: { weight: 'bold' },
          formatter: (value: number, context: any) => {
            const data: number[] = context.dataset?.data || [];
            const sum = data.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
            if (!sum) return '';
            const pct = (value / sum) * 100;
            return pct >= 5 ? `${pct.toFixed(1)}%` : '';
          },
          clip: true,
        },
      },
    }),
    []
  );

  const barChartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 800, easing: 'easeOutQuart' },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${numberFmt(ctx.parsed.y ?? ctx.parsed)}`,
          },
        },
        datalabels: {
          anchor: 'end',
          align: 'end',
          color: '#444',
          font: { weight: 'bold' as const },
          formatter: (value: number) => (value >= 1 ? numberFmt(value) : ''),
          clamp: true,
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 0 } },
        y: {
          grid: { display: false },
          beginAtZero: true,
          ticks: {
            precision: 0,
            callback: (v: any) => numberFmt(v),
          },
        },
      },
    }),
    []
  );

  const channelPieOptions = baseDonutOptions;
  const donutChartOptions = baseDonutOptions;

  // ---------- Render ----------
  return (
    <div className="dashboard-grid">
      {/* LINHA 1: KPIs + Fornecedor */}
      <div className="kpi-group">
        <KpiCard title="Processos Ativos" value={kpis.active_processes ?? 0} icon="🚀" />
        <KpiCard
          title="Performance de Entrega (12 meses)"
          value={`${kpis.on_time_percentage ?? 0}%`}
          icon="✅"
        />
      </div>

      <div className="widget-supplier">
        <ChartContainer title="Processos Ativos por Fornecedor">
          {loading ? (
            <p>Carregando...</p>
          ) : supplierData.labels && supplierData.labels.length > 0 ? (
            <Doughnut data={supplierData} options={donutChartOptions} />
          ) : (
            <p>Sem dados para exibir.</p>
          )}
        </ChartContainer>
      </div>

      {/* LINHA 2: Planejador + Status */}
      <div className="widget-planner">
        <ChartContainer title="Carga de Trabalho por Planejador">
          {loading ? (
            <p>Carregando...</p>
          ) : plannerData.labels && plannerData.labels.length > 0 ? (
            <Doughnut data={plannerData} options={donutChartOptions} />
          ) : (
            <p>Sem dados para exibir.</p>
          )}
        </ChartContainer>
      </div>

      <div className="widget-status">
        <ChartContainer title="Processos por Status (Ativos)">
          {loading ? (
            <p>Carregando...</p>
          ) : statusData.labels && statusData.labels.length > 0 ? (
            <Bar data={statusData} options={{ ...barChartOptions, indexAxis: 'x' }} />
          ) : (
            <p>Sem dados para exibir.</p>
          )}
        </ChartContainer>
      </div>

      {/* LINHA 3: Cadastro + Canal de Desembaraço */}
      <div className="widget-cadastro">
        <ChartContainer title="Processos por Situação do Cadastro">
          {loading ? (
            <p>Carregando...</p>
          ) : cadastroData.labels && cadastroData.labels.length > 0 ? (
            <Bar data={cadastroData} options={barChartOptions} />
          ) : (
            <p>Sem dados para exibir.</p>
          )}
        </ChartContainer>
      </div>

      <div className="widget-desembaraco-channels">
        <ChartContainer title="Processos por Canal de Desembaraço">
          {loading ? (
            <p>Carregando...</p>
          ) : channelChartData.labels && channelChartData.labels.length > 0 ? (
            <Doughnut data={channelChartData} options={channelPieOptions} />
          ) : error ? (
            <p>{error}</p>
          ) : (
            <p>Sem dados para exibir.</p>
          )}
        </ChartContainer>
      </div>

      {/* LINHA 4: Lead Time */}
      <div className="widget-leadtime">
        <ChartContainer title="Análise de Lead Time por Fornecedor e Armador">
          <div className="leadtime-table-container">
            <table className="leadtime-table">
              <thead>
                <tr>
                  <th>Fornecedor</th>
                  <th>Transportador</th>
                  <th>Média TT (dias)</th>
                  <th>Média Delivery (dias)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4}>Carregando...</td>
                  </tr>
                ) : leadTimeData.length > 0 ? (
                  leadTimeData.map((item, index) => (
                    <tr key={`${item.supplier}-${item.shipper}-${index}`}>
                      <td>{item.supplier}</td>
                      <td>{item.shipper}</td>
                      <td>{numberFmt(item.avg_tt)}</td>
                      <td>{numberFmt(item.avg_delivery_days)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>Sem dados para exibir.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartContainer>
      </div>
    </div>
  );
};